import React, { useEffect, useState } from "react";

export interface TrainingStep {
  title: string;
  description: string;
  tip?: string;
  highlight?: string; // CSS selector to highlight
}

interface TrainingGuideProps {
  moduleKey: string;
  steps: TrainingStep[];
  isOpen: boolean;
  onClose: () => void;
}

const TrainingGuide: React.FC<TrainingGuideProps> = ({ moduleKey, steps, isOpen, onClose }) => {
  const storageKey = `training_completed_${moduleKey}`;
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(false);

  // Load saved progress
  useEffect(() => {
    if (isOpen) {
      const saved = localStorage.getItem(storageKey);
      if (saved === "done") {
        setCompleted(true);
        setCurrentStep(0);
      } else {
        const savedStep = parseInt(localStorage.getItem(`training_step_${moduleKey}`) || "0", 10);
        setCurrentStep(Math.min(savedStep, steps.length - 1));
        setCompleted(false);
      }
    }
  }, [isOpen, moduleKey, steps.length, storageKey]);

  // Highlight element when step changes
  useEffect(() => {
    if (!isOpen) return;
    const step = steps[currentStep];
    if (!step?.highlight) return;

    const el = document.querySelector(step.highlight) as HTMLElement | null;
    if (el) {
      el.style.outline = "2px solid #ffd6a5";
      el.style.outlineOffset = "3px";
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    return () => {
      if (el) {
        el.style.outline = "";
        el.style.outlineOffset = "";
      }
    };
  }, [currentStep, isOpen, steps]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      localStorage.setItem(`training_step_${moduleKey}`, String(next));
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      localStorage.setItem(`training_step_${moduleKey}`, String(prev));
    }
  };

  const handleDone = () => {
    localStorage.setItem(storageKey, "done");
    localStorage.removeItem(`training_step_${moduleKey}`);
    setCompleted(true);
    onClose();
  };

  const handleReset = () => {
    localStorage.removeItem(storageKey);
    localStorage.setItem(`training_step_${moduleKey}`, "0");
    setCurrentStep(0);
    setCompleted(false);
  };

  if (!isOpen) return null;

  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;
  const isLast = currentStep === steps.length - 1;

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 999,
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Panel */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: 380,
        background: "linear-gradient(180deg, #0e1830 0%, #0b1326 100%)",
        border: "1px solid rgba(255,214,165,0.15)",
        borderRight: "none",
        zIndex: 1000,
        display: "flex", flexDirection: "column",
        fontFamily: "'Manrope', 'Space Grotesk', sans-serif",
        boxShadow: "-20px 0 60px rgba(0,0,0,0.5)",
        animation: "slideInRight 0.25s ease-out",
      }}>
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); opacity: 0; }
            to   { transform: translateX(0);    opacity: 1; }
          }
        `}</style>

        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px 14px",
          borderBottom: "1px solid rgba(255,214,165,0.1)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>📖</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#ffd6a5" }}>Training Guide</span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", color: "#6b7280",
              cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 4,
            }}
          >×</button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 3, background: "rgba(255,255,255,0.06)" }}>
          <div style={{
            height: "100%",
            width: `${progress}%`,
            background: "linear-gradient(90deg, #ffd6a5, #e7c090)",
            borderRadius: "0 3px 3px 0",
            transition: "width 0.3s ease",
          }} />
        </div>

        {/* Step counter */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 20px 6px",
        }}>
          <span style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Step {currentStep + 1} of {steps.length}
          </span>
          {completed && (
            <span style={{ fontSize: 11, color: "#22c55e", fontWeight: 600 }}>✓ Completed</span>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 20px 20px" }}>
          <div style={{
            background: "rgba(255,214,165,0.05)",
            border: "1px solid rgba(255,214,165,0.12)",
            borderRadius: 12,
            padding: "18px 16px",
            marginBottom: 12,
          }}>
            <h3 style={{
              margin: "0 0 10px",
              fontSize: 15,
              fontWeight: 700,
              color: "#ffd6a5",
              lineHeight: 1.3,
            }}>
              {step.title}
            </h3>
            <p style={{
              margin: 0,
              fontSize: 13,
              color: "#c4b8a8",
              lineHeight: 1.7,
            }}>
              {step.description}
            </p>
          </div>

          {step.tip && (
            <div style={{
              background: "rgba(34,197,94,0.07)",
              border: "1px solid rgba(34,197,94,0.2)",
              borderRadius: 8,
              padding: "10px 14px",
              display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>💡</span>
              <p style={{ margin: 0, fontSize: 12, color: "#86efac", lineHeight: 1.6 }}>
                {step.tip}
              </p>
            </div>
          )}
        </div>

        {/* Step dots */}
        <div style={{
          display: "flex", justifyContent: "center", gap: 6,
          padding: "8px 20px",
        }}>
          {steps.map((_, i) => (
            <button
              key={i}
              onClick={() => { setCurrentStep(i); localStorage.setItem(`training_step_${moduleKey}`, String(i)); }}
              style={{
                width: i === currentStep ? 20 : 7,
                height: 7,
                borderRadius: 99,
                border: "none",
                cursor: "pointer",
                background: i === currentStep ? "#ffd6a5" : i < currentStep ? "#9b8f83" : "rgba(255,255,255,0.12)",
                transition: "all 0.2s ease",
                padding: 0,
              }}
            />
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", gap: 8, padding: "14px 20px 20px",
          borderTop: "1px solid rgba(255,214,165,0.08)",
        }}>
          <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            style={{
              flex: 1, padding: "9px 0",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8, color: currentStep === 0 ? "#3a3a3a" : "#9b8f83",
              cursor: currentStep === 0 ? "not-allowed" : "pointer",
              fontSize: 13, fontWeight: 600,
            }}
          >← Prev</button>

          {isLast ? (
            <button
              onClick={handleDone}
              style={{
                flex: 2, padding: "9px 0",
                background: "linear-gradient(135deg, #ffd6a5, #e7c090)",
                border: "none", borderRadius: 8,
                color: "#0b1326", cursor: "pointer",
                fontSize: 13, fontWeight: 700,
              }}
            >Done ✓</button>
          ) : (
            <button
              onClick={handleNext}
              style={{
                flex: 2, padding: "9px 0",
                background: "linear-gradient(135deg, #ffd6a5, #e7c090)",
                border: "none", borderRadius: 8,
                color: "#0b1326", cursor: "pointer",
                fontSize: 13, fontWeight: 700,
              }}
            >Next →</button>
          )}
        </div>

        {/* Reset link */}
        {completed && (
          <div style={{ padding: "0 20px 14px", textAlign: "center" }}>
            <button
              onClick={handleReset}
              style={{
                background: "none", border: "none",
                color: "#4a5568", cursor: "pointer",
                fontSize: 11, textDecoration: "underline",
              }}
            >Restart guide</button>
          </div>
        )}
      </div>
    </>
  );
};

export default TrainingGuide;
